import { DateTime } from 'luxon'
import { ParsedQuery, parse, stringify } from 'query-string'

export const toQueryParams = (obj: any): string => {
  // Convert all date values to ISO strings
  Object.keys(obj).forEach((k) => {
    if (obj[k] instanceof Date) {
      obj[k] = obj[k].toISOString()
    }
  })
  return stringify(obj, { arrayFormat: 'none', sort: (a, b) => (a > b ? 1 : 0) })
}

const toSingleValue = <T = string>(value: T | T[] | null | undefined): string => {
  if (value instanceof Date) {
    return value.toISOString()
  }
  if (typeof value === 'string') {
    return value
  }
  if (Array.isArray(value) && typeof value[0] === 'string') {
    return value[0] || ''
  }
  if (Array.isArray(value) && 'toString' in (value[0] as any)) {
    return value[0]?.toString() || ''
  }
  if ('toString' in (value as any)) {
    return value?.toString() || ''
  }
  return ''
}

const toMultipleValues = <T = string | string[]>(
  value: T | T[] | null | undefined,
): string | string[] => {
  if (value instanceof Date) {
    return value.toISOString()
  }
  if (typeof value === 'string') {
    return value
  }
  if (Array.isArray(value)) {
    const values = value.map((v) => v?.toString() || '').filter(Boolean)
    return values.length > 0 ? values : ''
  }
  if ('toString' in (value as any)) {
    return value?.toString() || ''
  }
  return ''
}

const fromParsedString = <T = string>(
  value: T | T[] | null | undefined,
  options?: ParseParamsOptions,
): string | string[] | number | boolean | Date => {
  const val = options?.parseArray ? toMultipleValues<T>(value) : toSingleValue<T>(value)
  if (Array.isArray(val)) {
    return val
  }
  const isValidBoolean =
    typeof value !== 'undefined' && value !== null && (val === 'true' || val === 'false')
  if (isValidBoolean) {
    return val === 'true'
  }
  const isValidNumber = typeof value !== 'undefined' && value !== null && !isNaN(Number(val))
  if (isValidNumber && options?.parseNumber) {
    return Number(val)
  }
  const isValidDate =
    typeof value !== 'undefined' && value !== null && DateTime.fromISO(val).isValid
  if (isValidDate && options?.parseDate) {
    return DateTime.fromISO(val).toJSDate()
  }
  return val
}

interface ParseParamsOptions {
  parseDate?: boolean
  parseNumber?: boolean
  parseBoolean?: boolean
  parseArray?: boolean
}
export function fromQueryParams(
  value: string,
  options: { parseBoolean: true } & ParseParamsOptions,
): ParsedQuery<string | boolean>
export function fromQueryParams(
  value: string,
  options: { parseNumber: true } & ParseParamsOptions,
): ParsedQuery<string | number>
export function fromQueryParams(
  value: string,
  options: { parseNumber: true; parseBoolean: true } & ParseParamsOptions,
): ParsedQuery<string | number | boolean>
export function fromQueryParams(
  value: string,
  options: { parseDate: true; parseBoolean: false } & ParseParamsOptions,
): ParsedQuery<string | Date>
export function fromQueryParams(
  value: string,
  options: { parseDate: true; parseBoolean: true } & ParseParamsOptions,
): ParsedQuery<string | Date | boolean>
export function fromQueryParams(
  value: string,
  options: { parseDate: true; parseNumber: true; parseBoolean: false } & ParseParamsOptions,
): ParsedQuery<string | Date | number>
export function fromQueryParams(
  value: string,
  options: { parseDate: true; parseNumber: true; parseBoolean: true } & ParseParamsOptions,
): ParsedQuery<string | Date | number | boolean>
export function fromQueryParams(
  value: string,
  options: { parseDate: false; parseNumber: false; parseBoolean: false } & ParseParamsOptions,
): ParsedQuery<string>
export function fromQueryParams(
  value: string,
  options: { parseBoolean: true; parseArray: true } & ParseParamsOptions,
): ParsedQuery<string | string[] | boolean>
export function fromQueryParams(
  value: string,
  options: { parseNumber: true; parseArray: true } & ParseParamsOptions,
): ParsedQuery<string | string[] | number>
export function fromQueryParams(
  value: string,
  options: { parseNumber: true; parseBoolean: true; parseArray: true } & ParseParamsOptions,
): ParsedQuery<string | string[] | number | boolean>
export function fromQueryParams(
  value: string,
  options: { parseDate: true; parseBoolean: false; parseArray: true } & ParseParamsOptions,
): ParsedQuery<string | string[] | Date>
export function fromQueryParams(
  value: string,
  options: { parseDate: true; parseBoolean: true; parseArray: true } & ParseParamsOptions,
): ParsedQuery<string | string[] | Date | boolean>
export function fromQueryParams(
  value: string,
  options: {
    parseDate: true
    parseNumber: true
    parseBoolean: false
    parseArray: true
  } & ParseParamsOptions,
): ParsedQuery<string | string[] | Date | number>
export function fromQueryParams(
  value: string,
  options: {
    parseDate: true
    parseNumber: true
    parseBoolean: true
    parseArray: true
  } & ParseParamsOptions,
): ParsedQuery<string | string[] | Date | number | boolean>
export function fromQueryParams(
  value: string,
  options: {
    parseDate: false
    parseNumber: false
    parseBoolean: false
    parseArray: true
  } & ParseParamsOptions,
): ParsedQuery<string | string[]>
export function fromQueryParams(value: string, options?: ParseParamsOptions): ParsedQuery
export function fromQueryParams(value: string, options?: ParseParamsOptions) {
  const { parseDate = false, parseNumber = false } = options || {}
  const obj = parse(value)

  if (parseDate || parseNumber) {
    const i: ParsedQuery<string | string[] | number | boolean | Date> = {}
    Object.keys(obj).forEach((k) => {
      i[k] = fromParsedString(obj[k], options)
    })
    return i
  }
  const i: ParsedQuery = {}
  Object.keys(obj).forEach((k) => {
    i[k] = toSingleValue(obj[k])
  })
  return i
}

export function toSingleQueryValue<T = string>(
  query: Record<string, T | T[] | null | undefined>,
  options: { parseDate: true } & ParseParamsOptions,
): Record<string, string | Date>
export function toSingleQueryValue<T = string>(
  query: Record<string, T | T[] | null | undefined>,
  options?: ParseParamsOptions,
): Record<string, string>
export function toSingleQueryValue<T = string>(
  query: Record<string, T | T[] | null | undefined>,
  options?: ParseParamsOptions,
) {
  const { parseDate, parseNumber, parseBoolean, parseArray } = options || {}
  if (parseDate || parseNumber || parseBoolean || parseArray) {
    const i: Record<string, string | string[] | number | boolean | Date> = {}
    Object.keys(query).forEach((k) => (i[k] = fromParsedString(query[k], options)))
    return i
  }
  const i: Record<string, string> = {}
  Object.keys(query).forEach((k) => (i[k] = toSingleValue(query[k])))
  return i
}
